import type { CrewlinkClient } from '../supabase-client';
import type { SolMemoryKind, SolMemoryRow } from '../database.types';

/**
 * Writer wrappers for the long-term memory writers of migration 0023 (M11 /
 * D-040): forget one, replace one (edit / correct, by supersession), clear all.
 *
 * The layering rule (ARCHITECTURE §2, INV-T02): UI → query hooks → writer
 * wrappers → Postgres RPC. Ownership comes from `auth.uid()` inside the
 * function (INV-T23). **These reach `sol_memory` and nothing else** — no
 * Capture, Project, status, report or Work Log writer is in the same authority
 * path, and the operational writers cannot reach memory.
 *
 * **Clear is behind a confirmation on the screen** (D-040); the function
 * itself clears the caller's own active memories in one statement.
 */

export async function forgetSolMemory(
  client: CrewlinkClient,
  memoryId: string,
): Promise<SolMemoryRow> {
  const { data, error } = await client.rpc('forget_sol_memory', { p_memory_id: memoryId });
  if (error) throw error;
  if (!data) throw new Error('forget_sol_memory returned no row');
  return data;
}

/**
 * Edit / correct a memory. **Supersession, not mutation**: the old row becomes
 * `superseded` pointing at the new one, and the new one is EXPLICIT — he wrote
 * the words himself.
 */
export async function replaceSolMemory(
  client: CrewlinkClient,
  targetId: string,
  kind: SolMemoryKind,
  content: string,
): Promise<SolMemoryRow> {
  const { data, error } = await client.rpc('replace_sol_memory', {
    p_target_id: targetId,
    p_kind: kind,
    p_origin: 'explicit',
    p_content: content,
    p_source_conversation_id: null,
    p_project_id: null,
  });
  if (error) throw error;
  if (!data) throw new Error('replace_sol_memory returned no row');
  return data;
}

/** Clear every active memory. Returns how many were cleared. */
export async function clearSolMemories(client: CrewlinkClient): Promise<number> {
  const { data, error } = await client.rpc('clear_sol_memories', {});
  if (error) throw error;
  return typeof data === 'number' ? data : 0;
}

/** Turn a failure into something Brandon can act on. Never a code, never a UUID. */
export function describeMemoryWriteError(message: string): string {
  const normalised = message.toLowerCase();
  if (normalised.includes('not found') || normalised.includes('p0002')) {
    return 'That memory is no longer there.';
  }
  if (normalised.includes('not active') || normalised.includes('55000')) {
    return 'That memory was already changed. Showing the current list.';
  }
  if (normalised.includes('not authenticated') || normalised.includes('42501')) {
    return 'Your session has expired. Sign in again and try once more.';
  }
  if (normalised.includes('1 to 500') || normalised.includes('22001')) {
    return 'A memory must be between 1 and 500 characters.';
  }
  if (normalised.includes('failed to fetch') || normalised.includes('network')) {
    return 'Could not reach Crewlink. Check your connection and try again.';
  }
  return 'Could not change that memory. Please try again.';
}
